import * as Sentry from "@sentry/react";
import React from "react";
import { useSearchParams } from "react-router-dom";

import { Tabs } from "@performyard/py-components";

import { AdminContainer, AdminTitle } from "../../../../components/Admin";
import PageTitle from "../../../../components/PageTitle";
import Spacer from "../../../../components/Spacer";
import AIInsightsEmployeesSettingsTab from "./Employees";
import AIInsightsEngagementSettingsTab from "./Engagement";
import AIInsightsMeetingsSettingsTab from "./Meetings";
import AIInsightsReviewsSettingsTab from "./Reviews";
import AIInsightsSurveysSettingsTab from "./Surveys";

import { getFeatureFlagQuery, useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

import { companyAISettingsQuery, currentUserQuery } from "../../../../api/queries";
import { dataLoader } from "../../../../api/queries/dataLoader";

import ErrorPage from "../../../Error";

export const aIInsightsSettingsLoader = () => {
	const queries = [companyAISettingsQuery, getFeatureFlagQuery];

	return dataLoader([[currentUserQuery], queries]);
};

const AIInsightsSettings = () => {
	const [searchParams] = useSearchParams();

	// feature flags
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: reviewsEnabled } = useFeatureFlagQuery("ff-is-reviews-enabled", false);
	const { value: engagementSurveyEnabled } = useFeatureFlagQuery("ff-is-engagement-survey-enabled", false);
	const { value: meetingsEnabled } = useFeatureFlagQuery("ff-is-meetings-enabled", false);
	const { value: surveysEnabled } = useFeatureFlagQuery("ff-is-surveys-enabled", false);
	const { value: employeeInsightsEnabled } = useFeatureFlagQuery("ff-temp-is-ai-employee-insights-enabled", false);

	const tabs = [
		...(reviewsEnabled ? [{ value: "reviews", label: "Reviews", content: <AIInsightsReviewsSettingsTab /> }] : []),
		...(engagementSurveyEnabled
			? [{ value: "engagement", label: "Engagement", content: <AIInsightsEngagementSettingsTab /> }]
			: []),
		...(meetingsEnabled
			? [{ value: "meetings", label: "Meetings", content: <AIInsightsMeetingsSettingsTab /> }]
			: []),
		...(surveysEnabled ? [{ value: "surveys", label: "Surveys", content: <AIInsightsSurveysSettingsTab /> }] : []),
		...(employeeInsightsEnabled
			? [{ value: "employees", label: "Employees", content: <AIInsightsEmployeesSettingsTab /> }]
			: []),
	];

	// If there are no tabs then user is force browsing (and doesn't have access to any AI Insights tabs).
	// There is never a case where there are no tabs in a normal scenario.
	if (tabs.length === 0 || !aiFeatureFlagEnabled) {
		return <ErrorPage code={404} />;
	}

	// Set the active tab based on the URL parameter
	// If the URL parameter is not a valid tab, default to first valid tab
	const validTabValues = tabs.map((tab) => tab.value);
	const tabFromUrl = searchParams.get("tab");
	const activeTab = tabFromUrl && validTabValues.includes(tabFromUrl) ? tabFromUrl : tabs[0].value;

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			<Spacer height={4} />
			<AdminContainer>
				<PageTitle title="AI Insights" />
				<AdminTitle>AI Insights</AdminTitle>
				<Spacer height={3} />
				<Tabs
					tabs={tabs}
					value={activeTab}
					urlParam="tab"
					color="neutral"
					size="large"
					dangerouslySetClassName="gap-10"
				/>
			</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default AIInsightsSettings;
